// Workspace landing — where a workspace's people land when they OPEN Stride.
//
// A workspace (utils/workspaces.js) may carry
//   landing: { path: '/customs/<slug>' | any in-app path, devices: 'all' | 'touch' }
// so, say, a shop-floor tablet opens straight on the workshop's simple page
// instead of Home. Home is still one click away (the logo / Home in the
// bar), which is how they reach the whole of Stride.
//
// THE RULE, all of it here and pure (app-shell/test/run-workspace-landing-tests.mjs):
//   · only an OPENING of Stride: the page was loaded at '/' (sign-in, the
//     installed app's start_url) or at Home (utils/landing.js records it,
//     once per page load). Never a deep link — a shared work item link
//     still opens the work item — and never a later click on Home;
//   · only the workspace the shell would show this person: the one they
//     switched to last (remembered), else on a first visit their own
//     workspace, else the main bar — pickWorkspace, the same choice the
//     bar makes. With several workspaces that is the FIRST of theirs in the
//     admin's order until they switch. The main bar has no landing;
//   · devices 'touch' = a touch-only device (pointer: coarse AND hover:
//     none — a tablet or phone, not a touch-screen laptop with a trackpad)
//     OR the phone rule (utils/phoneViewport.js: a narrow viewport, ?phone=1);
//   · Workspaces switched off, or no landing set: exactly what happens today.

import { visibleWorkspaces, pickWorkspace } from './workspaces.js';
import { isEntryPath } from './landing.js';
import { detectPhone, isTouchDevice } from './phoneViewport.js';

export const LANDING_DEVICES = ['all', 'touch'];
export const MAX_LANDING_PATH = 200;

// A landing path as the server stores it (server/workspaces.js mirrors this),
// or null: one leading '/', no host, no scheme, no spaces, not '/' or /auth/.
export function cleanLandingPath(raw) {
  if (typeof raw !== 'string') return null;
  const p = raw.trim();
  if (!p || p.length > MAX_LANDING_PATH || !p.startsWith('/') || p.startsWith('//')) return null;
  if (/[\\\s\u0000-\u001f\u007f]/.test(p) || p === '/' || /^\/auth(\/|$|\?|#)/.test(p)) return null;
  return p;
}

// The decision. `active` is the workspace the shell shows this person
// (useWorkspaces().active, or pickWorkspace below). → the path to open, or
// null for "carry on as today".
//   entryPath  the pathname this page load began at
//   entrySearch its query (?embedded / ?popout are never openings)
//   deepLink   a link followed while signed out is being taken
//   touch      this is a tablet / phone (detectTouch)
//   enabled    the client's WORKSPACES_ENABLED
export function workspaceLanding({ entryPath, entrySearch = '', deepLink = false, active, touch = false, enabled = true }) {
  if (!enabled || deepLink) return null;
  if (!isEntryPath(entryPath, entrySearch)) return null;
  if (!active || active.isMain || !active.landing) return null;
  const path = cleanLandingPath(active.landing.path);
  if (!path) return null;
  const devices = active.landing.devices || 'all';
  if (!LANDING_DEVICES.includes(devices)) return null;
  if (devices === 'touch' && !touch) return null;
  return path;
}

// The same, from the stored definition and the person's groups — the choice
// useWorkspaces makes, for the suite and anyone without the hook.
export function landingFor({ definition, userGroups, chosen = null, siteName = 'Main', ...rest }) {
  const visible = visibleWorkspaces(definition, userGroups || [], siteName);
  return workspaceLanding({ ...rest, active: pickWorkspace({ visible, requested: chosen }) });
}

// Is this a tablet or phone? Read live from the window.
export function detectTouch() {
  if (typeof window === 'undefined') return false;
  try { return isTouchDevice() || detectPhone(); } catch { return false; }
}

// The editor's suggestions: the in-app pages a workspace's own bar opens
// (links, and pages inside its menus), first one per address. `resolve` is
// navUrls.resolveNavUrl bound to this origin.
export function landingSuggestions(sidebar, resolve) {
  const out = [];
  const seen = new Set();
  const add = (label, raw) => {
    const p = cleanLandingPath(resolve(raw) || '');
    if (p && !seen.has(p)) { seen.add(p); out.push({ label: label || p, path: p }); }
  };
  const walk = (nodes) => (nodes || []).forEach((n) => {
    if (!n) return;
    if (n.url) add(n.label, n.url);
    if (Array.isArray(n.children)) walk(n.children);
  });
  (Array.isArray(sidebar) ? sidebar : []).forEach((item) => {
    if (!item) return;
    if (item.type === 'builtin' && item.id === 'zebra-simple-dashboards') add(item.label || 'Dashboards', '/dashboards');
    else if (item.type === 'custom' && item.style === 'menu') walk(item.menuTree);
    else if (item.type === 'custom') add(item.label, item.url);
  });
  return out;
}
